import { useState } from 'react';
import Button from '@/components/Button';
import SignupAccountCreateForm from '@/modules/auth/components/SignupAccountCreateForm';
import { SignupAccount } from '@/modules/auth/models/Signup';
import { withLoading } from '@/utils/async';
import { Radio, RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useForm } from 'react-hook-form';

interface SignupAccountFormProps {
  onSubmit(data: SignupAccount): Promise<void>;
}

const SignupAccountForm = ({ onSubmit }: SignupAccountFormProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignupAccount>({
    values: {
      businessType: 'retailer',
      businessName: '',
      action: 'create',
      street1: '',
      street2: '',
      city: '',
    },
  });

  const [loading, setLoading] = useState(false);

  const countryCode = watch('countryCode');

  register('countryCode', { required: true });

  const [selectedBusinessType, setSelectedBusinessType] = useState<'retailer' | 'supplier'>(
    'retailer',
  );

  const handleSignup = withLoading(onSubmit, setLoading);

  const setCountryCode = (countryCode?: string) => {
    setValue('countryCode', countryCode);
  };

  const businessTypes = [
    { name: 'Retailer', value: 'retailer' },
    { name: 'Supplier', value: 'supplier' },
  ];

  return (
    <form onSubmit={handleSubmit(handleSignup)} className="mx-auto w-full max-w-[620px]">
      <div className="flex w-full flex-col gap-4 rounded-lg border border-gray-100 bg-white p-6 shadow-lg shadow-gray-200/60 sm:gap-6 sm:p-10">
        <h1 className="mb-2 text-center text-2xl font-semibold tracking-tight text-gray-900">
          Business details
        </h1>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-600 mb-1.5">
            Choose Your Business Type:
          </label>
          <RadioGroup
            value={selectedBusinessType}
            onChange={(value) => {
              setSelectedBusinessType(value);
              setValue('businessType', value);
            }}
            className="grid grid-cols-2 gap-2"
          >
            {businessTypes.map((type) => (
              <Radio
                key={type.value}
                value={type.value}
                className={({ checked }) =>
                  `relative flex cursor-pointer rounded-md border px-5 py-4 transition ${
                    checked
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-200 bg-gray-50 text-gray-900 hover:border-blue-300'
                  }`
                }
              >
                {({ checked }) => (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <div className="text-sm">
                        <p className="font-semibold">{type.name}</p>
                      </div>
                      {checked && <CheckCircleIcon className="w-6 h-6 text-white" />}
                    </div>
                  </>
                )}
              </Radio>
            ))}
          </RadioGroup>
          {errors.businessType && (
            <p className="text-red-700 text-sm">Business type is required.</p>
          )}
        </div>

        <SignupAccountCreateForm
          register={register}
          errors={errors}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
        />

        <Button type="submit" width="full" rounded="full" size="lg" loading={loading}>
          Continue
        </Button>
      </div>
    </form>
  );
};

export default SignupAccountForm;
